import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';
import ReturningClientBanner from '../../components/portal/ReturningClientBanner';

const PortalLogin = () => {
  const [firmName, setFirmName] = useState('');
  useEffect(() => {
    fetch('/api/public/firm-info')
      .then(r => r.json())
      .then(d => setFirmName(d.firm_name || ''))
      .catch(() => {});
  }, []);

  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [contactName, setContactName] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (clientPortalService.isAuthenticated()) {
      navigate('/portal/dashboard', { replace: true });
    }
  }, [navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleResendCode = async () => {
    setError('');
    setResendCooldown(30);
    try {
      const response = await clientPortalService.login(email, password);
      if (response.requires_2fa) {
        setTempToken(response.temp_token);
        localStorage.removeItem('client_portal_token');
        localStorage.removeItem('client_portal_contact');
      }
      setError('A new code has been sent to your email.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code. Please try logging in again.');
      setNeeds2FA(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await clientPortalService.login(email, password);

      if (response.requires_2fa) {
        setNeeds2FA(true);
        setTempToken(response.temp_token);
        setContactName(response.contact_name || '');
        // Clear token that login() stored (it's a temp token, not valid for API calls)
        localStorage.removeItem('client_portal_token');
        localStorage.removeItem('client_portal_contact');
      } else {
        window.dispatchEvent(new CustomEvent('portal-login-success'));
        if (response.is_first_login || response.profile_needs_update) {
          navigate('/portal/profile-setup');
        } else if (!response.pin_created) {
          navigate('/portal/pin-setup');
        } else if (!response.intake_completed) {
          navigate('/portal/intake');
        } else {
          navigate('/portal/dashboard');
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Invalid email or password';
      setError(detail);
      if (detail.toLowerCase().includes('locked')) {
        window.dispatchEvent(new CustomEvent('portal-login-failed', { detail: { type: 'locked' } }));
      } else {
        window.dispatchEvent(new CustomEvent('portal-login-failed', { detail: { type: 'password' } }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await clientPortalService.verify2FALogin(tempToken, otpCode);
      window.dispatchEvent(new CustomEvent('portal-login-success'));

      if (response.must_change_password) {
        navigate('/portal/change-password');
      } else if (response.staging_incomplete) {
        navigate('/portal/staging');
      } else if (response.is_first_login || response.profile_needs_update) {
        navigate('/portal/profile-setup');
      } else if (!response.pin_created) {
        navigate('/portal/pin-setup');
      } else if (!response.intake_completed) {
        navigate('/portal/intake');
      } else {
        navigate('/portal/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
      window.dispatchEvent(new CustomEvent('portal-login-failed', { detail: { type: 'otp' } }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white tracking-tight mb-2">{firmName || 'Client Portal'}</h1>
          <p className="text-virgo-teal-300 mt-4">Sign in to your portal</p>
          {needs2FA && (
            <p className="text-virgo-teal-400 mt-2">Two-Factor Authentication</p>
          )}
        </div>

        {/* Login / 2FA Form */}
        <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-8">
          {needs2FA ? (
            <form onSubmit={handle2FASubmit} className="space-y-6">
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-cyan-500/20 mb-3">
                  <Shield className="w-6 h-6 text-cyan-400" />
                </div>
                <p className="text-virgo-teal-300 text-sm">
                  {contactName ? `Welcome back, ${contactName}` : 'Enter your verification code'}
                </p>
                <p className="text-virgo-teal-500 text-xs mt-1">
                  A verification code has been sent to your email — if you don't see it, check your spam folder
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  required
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={10}
                  className="w-full px-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white text-center text-2xl tracking-widest placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent font-mono"
                  placeholder="000000"
                />
              </div>

              <button
                type="submit"
                disabled={loading || otpCode.length < 6}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all shadow-lg shadow-cyan-500/30"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setNeeds2FA(false); setOtpCode(""); setError(""); }}
                  className="flex-1 py-2 text-virgo-teal-400 hover:text-white text-sm transition-colors"
                >
                  Back to login
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0}
                  className="flex-1 py-2 text-cyan-400 hover:text-cyan-300 text-sm transition-colors disabled:text-virgo-teal-600 disabled:cursor-not-allowed"
                >
                  {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : "Resend code"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <ReturningClientBanner variant="login" />
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-virgo-teal-300">
                    Password
                  </label>
                  <Link
                    to="/portal/forgot-password"
                    className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white placeholder-virgo-teal-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all shadow-lg shadow-cyan-500/30"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-virgo-teal-500 text-sm">
              Need access? Contact your attorney for portal credentials.
            </p>
          </div>

          {/* Cross-link to staff login */}
          <div className="mt-4 pt-4 border-t border-virgo-teal-700/30 text-center">
            <span className="text-virgo-teal-500 text-sm">Law firm staff? </span>
            <a href="/login" className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold transition-colors">Staff login</a>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-virgo-teal-500 text-sm">
          <p>{firmName || "Our Firm"} - Secure Client Portal</p>
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;